import * as React from "react";
import { FileText, Folder } from "lucide-react";
import { useDraggable } from "@dnd-kit/core";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDate } from "@/lib/format";
import { DocumentPreviewTooltip } from "./DocumentPreviewTooltip";

type DocumentStatus = "draft" | "published" | "archived";

type CheckboxCheckedState = boolean | "indeterminate";

type DocumentCardProps = {
  type: "document" | "folder";
  title: string;
  summary?: string;
  status?: DocumentStatus;
  updatedAt?: number;
  documentCount?: number;
  contentPreview?: string;
  tags?: string[];
  onClick: () => void;
  onDoubleClick?: () => void;
  documentId?: string;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
};

function statusBadgeClass(status: DocumentStatus): string {
  if (status === "published") return "border-emerald-300 text-emerald-700 bg-emerald-50";
  if (status === "archived") return "border-zinc-300 text-zinc-700 bg-zinc-50";
  return "border-amber-300 text-amber-700 bg-amber-50";
}

export function DocumentCard({
  type,
  title,
  summary,
  status,
  updatedAt,
  documentCount,
  contentPreview,
  tags,
  onClick,
  onDoubleClick,
  documentId,
  isSelected,
  onSelectionChange,
}: DocumentCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: documentId || `folder-${title}`,
    data: {
      type,
      documentId,
      title,
    },
    disabled: type === "folder",
  });
  const { role: _dragRole, tabIndex: _dragTabIndex, ...dragAttributes } = attributes;

  const handleClick = React.useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
      return;
    }
    onClick();
  }, [onClick]);

  const handleDoubleClick = React.useCallback(() => {
    if (onDoubleClick) {
      onDoubleClick();
      return;
    }
    onClick();
  }, [onClick, onDoubleClick]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent) => {
    if ((event.target as HTMLElement).closest('[role="checkbox"]')) {
      return;
    }
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onClick();
  }, [onClick]);

  const handleCheckboxChange = React.useCallback((checked: CheckboxCheckedState) => {
    if (onSelectionChange) {
      onSelectionChange(checked === true);
    }
  }, [onSelectionChange]);

  const cardContent = (
    <Card
      ref={setNodeRef}
      className={`cursor-pointer transition-all hover:border-primary hover:shadow-md ${
        isDragging ? "opacity-50" : ""
      }`}
      role="button"
      tabIndex={0}
      aria-label={type === "folder" ? `Open folder ${title}` : `Open document ${title}`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      {...(type === "document" ? listeners : {})}
      {...(type === "document" ? dragAttributes : {})}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          {type === "document" && onSelectionChange && (
            <div className="pt-0.5">
              <Checkbox
                checked={isSelected}
                onCheckedChange={handleCheckboxChange}
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                aria-label={`Select ${title}`}
              />
            </div>
          )}
          <div className="rounded-md border p-2">
            {type === "folder" ? (
              <Folder className="size-5 text-amber-600" />
            ) : (
              <FileText className="size-5 text-blue-600" />
            )}
          </div>
          <div className="flex-1 space-y-1">
            <CardTitle className="line-clamp-2 text-base">{title}</CardTitle>
            {summary && (
              <CardDescription className="line-clamp-2 text-xs">{summary}</CardDescription>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2 pt-0">
        {status && (
          <Badge variant="outline" className={statusBadgeClass(status)}>
            {status}
          </Badge>
        )}
        {updatedAt && (
          <Badge variant="outline" className="text-xs text-muted-foreground">
            {formatDate(updatedAt, { month: "short", day: "numeric" })}
          </Badge>
        )}
        {type === "folder" && documentCount !== undefined && (
          <Badge variant="outline" className="text-xs text-muted-foreground">
            {documentCount} {documentCount === 1 ? "doc" : "docs"}
          </Badge>
        )}
      </CardContent>
    </Card>
  );

  if (type === "document" && contentPreview) {
    return (
      <DocumentPreviewTooltip
        title={title}
        summary={summary}
        contentPreview={contentPreview}
        status={status}
        tags={tags}
        updatedAt={updatedAt}
      >
        {cardContent}
      </DocumentPreviewTooltip>
    );
  }

  return cardContent;
}
